import {
  ActionsMenu,
  PageContentLayout,
  PageHeaderLayout,
  Link,
  ListTotal,
  SideMenu,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { Divider, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  DateRangeFilter,
  FilterButton,
  Filters,
  ParentCompaniesFilter,
  SelectFilter,
} from 'components/filters';
import { APP, ROUTES } from 'config';
import {
  adminClientsFiltersSchema,
  useAdminClients,
} from 'store/admin-clients';
import { useSearchParams } from 'utils/navigation';
import { COLOR_BY_STATUS, companyStatuses } from 'store/companies';
import { formatCountry } from 'utils/format';
import { FC } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useModalContext } from 'store/modal';
import { CreateClientModal } from './atoms';

export const AdminClientsList: FC = () => {
  const { t, i18n } = useTranslation();
  const { path } = useRouteMatch();
  const { openModal } = useModalContext();

  const { searchParams: filters, navigate } = useSearchParams(
    adminClientsFiltersSchema,
  );

  const { data: clients, isLoading } = useAdminClients(filters);

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('menu.clients'),
          },
        ]}
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            tabs={[
              {
                key: 'clients',
                label: t('menu.clients'),
                url: ROUTES.admin.clients.list,
                isActive: path === ROUTES.admin.clients.list,
              },
            ]}
          />
        }
      >
        <TableWrapper>
          <TableToolbar>
            <Stack gap={1}>
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                gap={1}
              >
                <ActionsMenu
                  type="spread"
                  items={[
                    {
                      label: t('client.new_client'),
                      color: 'primary',
                      onClick: () => {
                        openModal({
                          component: CreateClientModal,
                        });
                      },
                    },
                  ]}
                />
              </Stack>

              <Divider />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
              >
                <ListTotal total={clients?.total} />

                <Stack direction="row" alignItems="center" gap={1}>
                  <Filters
                    value={filters}
                    initialValues={adminClientsFiltersSchema.parse({})}
                    onChange={(v) => navigate({ params: { ...v, page: 1 } })}
                  >
                    {({ value, initialValues, setFilter }) => (
                      <>
                        <FilterButton
                          label={t('employees.company')}
                          value={value.companies}
                          initialValue={initialValues.companies}
                          onChange={(v) => setFilter('companies', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <ParentCompaniesFilter
                              value={value}
                              onApplyFilter={applyFilter}
                              onClearFilter={clearFilter}
                              onCancel={close}
                              multiple
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('clients.reportPeriod')}
                          value={value.date}
                          initialValue={initialValues.date}
                          onChange={(v) => setFilter('date', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <DateRangeFilter
                              value={value}
                              onApply={applyFilter}
                              onClear={clearFilter}
                              onCancel={close}
                              withTimezone
                            />
                          )}
                        </FilterButton>

                        <FilterButton
                          label={t('common.status')}
                          value={value.status}
                          initialValue={initialValues.status}
                          onChange={(v) => setFilter('status', v)}
                        >
                          {({ value, applyFilter, clearFilter, close }) => (
                            <SelectFilter
                              name="status"
                              value={{ value }}
                              onApplyFilter={({ value }) => applyFilter(value)}
                              onClearFilter={clearFilter}
                              onCancel={close}
                              multiple={false}
                              searchable={false}
                              options={companyStatuses.map((status) => ({
                                key: status,
                                label: t(`status.${status}`),
                              }))}
                            />
                          )}
                        </FilterButton>
                      </>
                    )}
                  </Filters>
                </Stack>
              </Stack>
            </Stack>
          </TableToolbar>

          <Table
            data={clients?.items ?? []}
            isLoading={isLoading}
            columns={[
              {
                id: 'country',
                header: t('benefits.country'),
                cell: ({ row: { original: client } }) =>
                  formatCountry(i18n.language, client.country),
              },
              {
                id: 'name',
                header: t('companies.company_name'),
                cell: ({ row: { original: client } }) => (
                  <Link
                    label={client.name}
                    to={ROUTES.admin.clients.details.settings.replace(
                      ':id',
                      client.id,
                    )}
                  />
                ),
              },
              {
                id: 'activeMobileUsers',
                header: t('clients.activeMobileUsers'),
                cell: ({ row: { original: client } }) => client.activeEmployees,
                meta: { align: 'right' },
              },
              {
                id: 'activeEmployees',
                header: t('clients.activeEmployees'),
                cell: ({ row: { original: client } }) => client.activeEmployees,
                meta: { align: 'right' },
              },
              {
                id: 'inactiveEmployees',
                header: t('clients.inactiveEmployees'),
                cell: ({ row: { original: client } }) =>
                  client.inactiveEmployees,
                meta: { align: 'right' },
              },
              {
                id: 'internalComment',
                header: t('clients.comment'),
                cell: ({ row: { original: client } }) => client.internalComment,
              },
              {
                id: 'status',
                header: t('common.current_status'),
                cell: ({ row: { original: client } }) => (
                  <Typography color={COLOR_BY_STATUS[client.status]}>
                    {t(`status.${client.status}`)}
                  </Typography>
                ),
              },
            ]}
            pagination={
              clients && {
                count: clients.total,
                page: filters.page,
                pageSize: filters.pageSize,
                pageSizeOptions: APP.pagination.defaults.pageSize,
                onChange: (page, pageSize) => {
                  navigate({ params: { page, pageSize } });
                },
              }
            }
          />
        </TableWrapper>
      </PageContentLayout>
    </Stack>
  );
};
